import React, { useEffect, useState } from 'react'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/router'
import { Box, Icon, Tab, TabList, Tabs } from '@alto-avios/alto-ui'

import { usePlatformConfig, usePlatformRoutes } from '~/context/platform'
import { useFeatureFlag } from '~/context/featureFlag'
import { type ProductSlug } from '~/schemas/browserUrl'
import { isClient } from '~/utils/envChecks'
import {
  getExternalAncillaryUrl,
  shouldUseAerLingusExternalAncillaryLinks,
} from './externalAncillaryLinks'

import styles from './SearchPageNavigation.module.scss'

type Props = {
  activeSlug?: ProductSlug
  enabledSlugs?: Array<ProductSlug>
}

const SearchPageNavigation = ({ activeSlug, enabledSlugs }: Props) => {
  const t = useTranslations('common.product_labels')
  const router = useRouter()
  const { createRoutePath } = usePlatformRoutes()
  const { opCo, buildType, locale } = usePlatformConfig()
  const ancillaryLinksEnabled = useFeatureFlag('ancillaries-tabs-as-external-links')
  const [isHydrated, setIsHydrated] = useState(false)

  useEffect(() => {
    setIsHydrated(true)
  }, [])

  const productLabels: Record<ProductSlug, string> = {
    hotels: t('hotels'),
    cars: t('car_hire'),
    experiences: t('experiences'),
    flights: t('flights'),
  }
  const productIcons: Record<ProductSlug, string> = {
    hotels: 'hotel',
    cars: 'car',
    experiences: 'tickets',
    flights: 'plane',
  }
  // Temporary for e2e tests while not in higher envs
  // should remove this once feature is live and update tests to use new (alto generated) ids
  const legacyTabIds: Record<ProductSlug, string> = {
    hotels: 'hotels',
    cars: 'carhire',
    experiences: 'experiences',
    flights: 'flights',
  }
  const useAerLingusExternalLinks = shouldUseAerLingusExternalAncillaryLinks(
    ancillaryLinksEnabled,
    opCo,
    buildType
  )

  return (
    <Box
      width="100%"
      position="relative"
      justifyContent="center"
      alignItems="center"
      className={styles.tabsWrapper}
    >
      <Tabs
        styleVariant="contained"
        selectedKey={activeSlug}
        onSelectionChange={(key) => {
          const externalUrl = useAerLingusExternalLinks
            ? getExternalAncillaryUrl(key as ProductSlug, locale)
            : undefined
          if (externalUrl) {
            if (isClient()) {
              window.open(externalUrl, '_blank', 'noopener,noreferrer')
            }
            return
          }
          router.push(
            {
              pathname: createRoutePath(`/${key}`),
            },
            undefined,
            { scroll: false }
          )
        }}
      >
        <TabList>
          {enabledSlugs?.map((slug) => {
            const externalUrl = useAerLingusExternalLinks
              ? getExternalAncillaryUrl(slug, locale)
              : undefined
            return (
              <Tab id={slug} key={slug} iconName={isHydrated ? productIcons[slug] : undefined}>
                <span id={legacyTabIds[slug]}>
                  {productLabels[slug]}
                  {externalUrl ? (
                    <Icon
                      iconName="external-link"
                      role="img"
                      aria-label="Opens in a new tab"
                      style={{ marginInlineStart: '0.5rem' }}
                    />
                  ) : null}
                </span>
              </Tab>
            )
          })}
        </TabList>
      </Tabs>
    </Box>
  )
}

export default SearchPageNavigation
